import { useState } from "react";
import { PlayFilled } from "@digital-twins/design-system/icons";

import {
  exposureStripAssetLabel,
  productVersionLabel,
  respondentWordingFor,
  type ConceptAsset,
} from "@digital-twins/shared-types";

import { CreativeLightbox } from "@/components/chat/CreativeLightbox";
import { CreativeMedia } from "@/components/chat/CreativeMedia";

import { assetThumbnailCreative, isVideoMedia, primaryAssetMedia } from "../assets/assetMedia";

interface ExposureStripAssetsProps {
  /** The assets the zone shows, in catalog order. */
  assets: Array<ConceptAsset>;
  /** The study's content language, which a price's billing period is worded in. */
  contentLanguage?: string;
}

/**
 * The assets an exposure strip's zone shows twins. Each reads by the ordinal
 * twins read and its name ("Ad 1: the Hamilton film", `exposureStripAssetLabel`), a priced
 * product with its price as its matrix row reads it ("name · price",
 * `productVersionLabel`), so price versions that share the name are told
 * apart; with its image or video thumbnail. A click opens the shared lightbox,
 * which pages through the strip's media. An asset with no file shows its
 * label alone.
 */
export function ExposureStripAssets(props: ExposureStripAssetsProps) {
  const { assets, contentLanguage } = props;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const wording = respondentWordingFor(contentLanguage);
  const tiles = assets.map((asset) => ({
    asset,
    label: productVersionLabel(
      exposureStripAssetLabel(asset),
      "price" in asset ? asset.price : undefined,
      wording,
    ),
    creative: assetThumbnailCreative(asset),
  }));
  const creatives = tiles.flatMap((tile) => (tile.creative ? [tile.creative] : []));

  return (
    <div className="flex min-w-0 flex-wrap gap-2">
      {tiles.map(({ asset, label, creative }) =>
        creative ? (
          <button
            key={asset.id}
            type="button"
            className="flex min-w-0 max-w-full cursor-zoom-in items-center gap-2 rounded-[8px] border border-border/70 bg-background py-1 pl-1 pr-2.5 text-left text-[12px] font-medium text-foreground transition-colors hover:border-accent-highlight dark:bg-card"
            onClick={() => setOpenIndex(creatives.indexOf(creative))}
            data-testid={`questionnaire-exposure-strip-asset-${asset.id}`}
          >
            <span className="relative h-9 w-12 shrink-0 overflow-hidden rounded-[6px] border border-border">
              <CreativeMedia
                src={creative.src}
                mimeType={creative.mimeType}
                alt={label}
                className="h-full w-full object-cover object-top"
              />
              {isVideoMedia(primaryAssetMedia(asset)) ? (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 flex items-center justify-center"
                >
                  <PlayFilled className="h-3.5 w-3.5 text-white drop-shadow" />
                </span>
              ) : null}
            </span>
            <span className="truncate">{label}</span>
          </button>
        ) : (
          <span
            key={asset.id}
            className="flex min-w-0 max-w-full items-center rounded-[8px] border border-border/70 bg-background px-2.5 py-1.5 text-[12px] font-medium text-foreground dark:bg-card"
            data-testid={`questionnaire-exposure-strip-asset-${asset.id}`}
          >
            <span className="truncate">{label}</span>
          </span>
        ),
      )}
      {openIndex !== null ? (
        <CreativeLightbox
          creatives={creatives}
          index={openIndex}
          onIndex={setOpenIndex}
          onClose={() => setOpenIndex(null)}
          dataTestId="questionnaire-exposure-strip-lightbox"
        />
      ) : null}
    </div>
  );
}
